// MutaGo web client entry (M6-D3). The browser is a thin renderer over the
// Node gateway: it sends user intents and displays authoritative states; no
// rules logic lives here (apps/web/README.md authority boundary).
//
// Session selection: ?session=<id> rejoins an existing session (same browser
// tab identity grammar); otherwise a fresh id is minted. The gateway serves
// both the static bundle and the WebSocket endpoint on the same origin
// (--web), so the default URL is the page origin itself.

import { createRoot } from "react-dom/client";
import { createElement } from "react";
import { OfflineShell } from "./ui/OfflineShell.js";

import { GatewayClient } from "./core/client.js";
import { SessionStore } from "./core/store.js";
import { ID_GRAMMAR } from "./core/wire.js";
import { mintSessionId } from "./core/session-id.js";
import { PENDING_IMPORT_MARKER, parseStashedImport, type SgfMove } from "./core/sgf.js";
import { App } from "./ui/App.js";
import { modelSessionUrl, parseModelDirectory, selectedModelId } from "./core/model-directory.js";

function sessionIdFromLocation(): string {
  const param = new URLSearchParams(window.location.search).get("session");
  if (param !== null && param.length <= 128 && ID_GRAMMAR.test(param)) return param;
  return mintSessionId((n) => crypto.getRandomValues(new Uint8Array(n)));
}

const sessionId = sessionIdFromLocation();
const sessionUrl = new URL(window.location.href);
const directory = parseModelDirectory(document.getElementById("mutago-model-directory")?.textContent ?? null);
const currentModelId = selectedModelId(directory, sessionUrl, sessionId);
if (currentModelId) sessionUrl.searchParams.set("model", currentModelId);
sessionUrl.searchParams.set("session", sessionId);
window.history.replaceState(window.history.state, "", sessionUrl);
const store = new SessionStore(sessionId);
const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
// The commandId identity domain is per page instance: a random token keeps a
// rejoined session's journal from colliding with this instance's counter
// (same commandId with different bytes is COMMAND_ID_CONFLICT).
const clientToken = `web-${crypto.getRandomValues(new Uint8Array(4)).reduce((acc, b) => acc + b.toString(16).padStart(2, "0"), "")}`;
const client = new GatewayClient({
  url: `${protocol}//${window.location.host}${currentModelId ? "/models/" + currentModelId : "/"}`,
  store,
  socketFactory: (url) => new WebSocket(url),
  clientToken,
});

// GPU connections are started only by the explicit OfflineShell button.

// AppActions.connect is fire-and-forget; the client's promise rejects on
// transport failure, so the adapter consumes it (the store already shows
// "closed" and the connect button is the retry path).
const actions = {
  connect: () => {
    client.connect().catch(() => {});
  },
  submitAction: (actor: "BLACK" | "WHITE", actionId: number, kind: Parameters<typeof client.submitAction>[2]) =>
    client.submitAction(actor, actionId, kind),
  submitTermination: (actor: "BLACK" | "WHITE", kind: "RESIGNATION" | "TIMEOUT") =>
    client.submitTermination(actor, kind),
  requestSnapshot: () => client.requestSnapshot(),
  requestLegalMask: () => client.requestLegalMask(),
  requestExportLog: () => client.requestExportLog(),
  analysisStart: (p: number, d: number) => client.analysisStart(p, d),
  analysisStop: () => client.analysisStop(),
  controlModel: (enabled: boolean | null) => client.controlModel(enabled),
  // SGF import replays into a FRESH session: stash the parsed intents, mint a
  // new session id, and reload onto it (the store/client are per-session).
  startImport: (moves: SgfMove[]) => {
    const fresh = mintSessionId((n) => crypto.getRandomValues(new Uint8Array(n)));
    // The stash is bound to the fresh session id and carries the versioned
    // marker (produced only after the loss declaration was displayed).
    sessionStorage.setItem(
      "mutago.pendingImport",
      JSON.stringify({ marker: PENDING_IMPORT_MARKER, sessionId: fresh, moves }),
    );
    const url = new URL(window.location.href);
    url.searchParams.set("session", fresh);
    window.location.assign(url.toString());
  },
};

// After a reload onto a fresh session, resume a pending import once the
// baseline is live. The stashed intents are validated defensively (they came
// from our own parser, but storage is not a trusted channel).
function resumePendingImport(): void {
  const raw = sessionStorage.getItem("mutago.pendingImport");
  if (raw === null) return;
  const stashed = parseStashedImport(raw);
  if (stashed === null) {
    sessionStorage.removeItem("mutago.pendingImport");
    store.setImportStatus("导入缓存损坏,已丢弃");
    return;
  }
  // A stash bound to a different session belongs to another tab's flow:
  // leave it untouched rather than replaying into the wrong session.
  if (stashed.sessionId !== store.sessionId) return;
  sessionStorage.removeItem("mutago.pendingImport"); // single-shot, reload-safe
  const moves = stashed.moves;
  const unsubscribe = store.subscribe(() => {
    if (store.broken !== null) {
      unsubscribe();
      return;
    }
    if (store.phase !== "live" || store.tip === null) return;
    unsubscribe();
    // Freshness proof: the stash is bound to a 128-bit random session id
    // minted by THIS declaration page. A created:false answer can then only
    // be the idempotent hit of this page's own createSession whose ACK was
    // lost — no one else could hold a session under that id. (A same-origin
    // forgery could submit commands directly anyway; the declaration is a
    // UX honesty feature, not a security boundary.)
    void client.importReplay(moves);
  });
}
resumePendingImport();

const rootElement = document.getElementById("root");
if (rootElement === null) throw new Error("index.html is missing #root");
const onlineApp = createElement(App, { store, actions, models: directory.models,
  currentModelId,
  onNewGame: () => {
    if (!window.confirm("开始新对局？当前棋谱会保留。")) return;
    const url = new URL(window.location.href);
    url.searchParams.set("session", mintSessionId((n) => crypto.getRandomValues(new Uint8Array(n))));
    window.location.assign(url.toString());
  },
  onModelSelect: (model) => {
    if (model.id === currentModelId) return;
    if (!window.confirm(`切换到 ${model.label} 并开始新对局？当前对局将保留。`)) return;
    const fresh = mintSessionId((n) => crypto.getRandomValues(new Uint8Array(n)));
    window.location.assign(modelSessionUrl(window.location.href, model, fresh));
  },
});
createRoot(rootElement).render(createElement(OfflineShell, { client, store, onlineApp }));
